/**
 * 알림 모아보기 — **종류 전용 목록.** (Pages/Admin/Tasks.js 가 쓴다)
 *
 * 종류 칩 하나를 고르면 목록이 원장이 아니라 그 종류의 **소스 전체**로 바뀐다
 * (`/adm/ajax/tasks/source` → `LedgerProvider::source`). 옛 도메인 목록 화면 — 제재관리
 * 짜고치기·재발주 탭, 악덕제보 심사목록, 탈퇴신청 목록 — 을 이 자리가 대신한다.
 * 그래서 검색조건도 그 화면들의 것을 그대로 가져왔다.
 *
 * 종류마다 셋을 정한다.
 *   filters   검색조건 — 이름(key)·기본값(def)은 서버 Provider::source 가 읽는 이름과 짝이다.
 *             seg(칩, 하나만) · select · text(엔터·검색) · hidden(주소로만 들어오는 값, 칩으로 보이고 ✕ 로 푼다)
 *             seg·select 는 셋째 줄에 일자로 선다 — seg 의 `label` 이 머리말이고, 「전체」가 있으면 **맨 앞 칸**이다
 *             `columns: true` 인 조건은 칸 모양을 바꾼다(바뀌면 표를 새로 세운다)
 *   columns   가운데 칸. 담당·알림(앞)과 ⋯(뒤)는 Tasks.js 가 붙인다. x = 소스 행(d.detail)
 *   tools     조건줄 오른쪽 버튼 (옛 화면에 있던 손잡이)
 *   defaultState (선택) 들어올 때 미리 고를 상태 칩. 없으면 비워 소스 전체를 보여준다
 *
 * 여기에 없는 종류는 「전체」와 같은 공용 목록으로 그린다(고장이 아니다).
 */
import LayoutData from '../../../F/Admin/LayoutData';
const dash = '<span class="text-muted">-</span>';
const C    = () => $j.F.Admin.Collusion.Case;   // 판정창 모듈 — 점수색·표시·상태 말을 같이 쓴다

const Views = {

    /** 부정발주 — 옛 제재관리 짜고치기·재발주 탭 */
    collusion: {
        filters: [
            // 기본은 「전체」 — 종류 칩의 수는 두 유형을 합쳐 세므로, 한쪽만 보여주면 나머지가 안 보인다
            { key: 'cs_type', type: 'seg', label: '유형', def: '', columns: true,
              options: [['', '전체'], ['pair', '짜고치기'], ['reorder', '재발주']] },
            { key: 'status', type: 'seg', label: '판정', def: 'open',
              options: [['all', '전체'], ['open', '미판정'], ['clear', '정상'], ['punish', '제재']] },
            { key: 'minScore', type: 'select', label: '의심도', def: '40',
              options: [['', '전체'], ['70', '70점 이상'], ['50', '50점 이상'], ['40', '40점 이상'], ['30', '30점 이상']] },
            { key: 'sortBy', type: 'select', label: '정렬', def: 'score',
              options: [['score', '의심도순'], ['deals', '거래건수순'], ['msix', '-6차 취득순'], ['recent', '최근순']] },
            { key: 'keyword', type: 'text', placeholder: '회원명 또는 회원번호', width: 170 },
            // 제재관리 현황의 「무리」를 누르면 그 무리의 쌍만 본다 (?ring=N)
            { key: 'ring', type: 'hidden', label: (v) => `무리 ${v}` },
        ],
        columns: (ui, f) => {
            const score = { title: '의심도', className: 'text-center', width: '64px',
                render: (x) => `<span class="co-score ${C().scoreCls(x.score)}">${x.score}</span>` };
            const judge = { title: '판정', className: 'text-center', width: '70px', render: (x) => C().statusBadge(x) };

            const isPair  = (x) => x.cs_type === 'pair';
            const reorderRoute = (x) => {
                const [src, dst] = String(x.cs_key || '').split('-');
                return `${ui.order(src)} <i class="fas fa-arrow-right text-muted mx-1"></i> ${ui.order(dst)}`;
            };

            // 전체 — 두 유형이 섞인다. 칸은 공통만 두고, 근거·기간은 줄마다 그 유형의 말로 적는다
            if (!f.cs_type) return [
                score,
                { title: '유형', className: 'text-center tk-nowrap', width: '70px',
                  render: (x) => isPair(x) ? '짜고치기' : '재발주' },
                { title: '발주자', render: (x) => ui.member(x.mb_a, x.a_name, x.a_sido) },
                { title: '수주자',
                  render: (x) => Number(x.mb_b) > 0 ? ui.member(x.mb_b, x.b_name, x.b_sido) : '<span class="text-muted">미수주</span>' },
                { title: '근거', className: 'text-center tk-nowrap', width: '170px',
                  render: (x) => isPair(x) ? `거래(-6차) ${x.deal_cnt}건` : reorderRoute(x) },
                { title: '기간', className: 'text-center tk-nowrap', width: '120px',
                  render: (x) => isPair(x)
                      ? `<span class="text-muted">${ui.esc((x.first_day || '').slice(5))} ~ ${ui.esc((x.last_day || '').slice(5))}</span>`
                      : ui.esc(x.last_day) },
                { title: '표시', className: 'text-center', width: '90px', render: (x) => C().flags(x) },
                judge,
            ];

            if (f.cs_type === 'reorder') return [
                score,
                { title: '재발주한 회원', render: (x) => ui.member(x.mb_a, x.a_name, x.a_sido) },
                { title: '재발주를 잡은 회원',
                  render: (x) => Number(x.mb_b) > 0 ? ui.member(x.mb_b, x.b_name, x.b_sido) : '<span class="text-muted">미수주</span>' },
                { title: '오더', className: 'text-center tk-nowrap', width: '150px', render: reorderRoute },
                { title: '작업일', className: 'text-center tk-nowrap', width: '90px', render: (x) => ui.esc(x.last_day) },
                { title: '현장', render: (x) => ui.esc((x.detail || {}).address || '') || dash },
                { title: '금액', className: 'text-right tk-nowrap', width: '100px', render: (x) => ui.won(x.amount) },
                judge,
            ];

            return [
                score,
                { title: '발주자', render: (x) => ui.member(x.mb_a, x.a_name, x.a_sido) },
                { title: '수주자', render: (x) => ui.member(x.mb_b, x.b_name, x.b_sido) },
                // 모수가 "-6차가 붙은 거래" 라 거래건수 = 취득 -6차 건수다
                { title: '거래(-6차)', className: 'text-center', width: '80px', render: (x) => `${x.deal_cnt}건` },
                { title: '집중도', className: 'text-center tk-nowrap', width: '150px',
                  render: (x) => {
                      const d = x.detail || {};
                      return `<span class="co-share" title="발주자의 발주 ${d.balTotal}건 중">발주 ${d.balShare}%</span>
                              <span class="co-share" title="수주자의 수주 ${d.sooTotal}건 중">수주 ${d.sooShare}%</span>`;
                  } },
                { title: '기간', className: 'text-center tk-nowrap', width: '120px',
                  render: (x) => `<span class="text-muted">${ui.esc((x.first_day || '').slice(5))} ~ ${ui.esc((x.last_day || '').slice(5))}</span>` },
                { title: '표시', className: 'text-center', width: '90px', render: (x) => C().flags(x) },
                judge,
            ];
        },
    },

    /** 악덕 — 옛 악덕제보 심사목록(/adm/devil/reports) */
    devil: {
        // 첫 화면은 아직 안 끝난 알림만 — 칩을 풀면 제보 전체가 나온다
        defaultState: ['new', 'doing'],
        filters: [
            { key: 'status', type: 'seg', label: '신고상태', def: '',
              options: () => [['', '전체']].concat(
                  Object.entries(LayoutData.get('dvStatus', {}).report || {}).filter(([k]) => k !== 'DRAFT')) },
            { key: 'company', type: 'text', placeholder: '업체명·전화', width: 150 },
            { key: 'reporter', type: 'text', placeholder: '제보자명·전화', width: 150 },
        ],
        tools: [
            { label: '테스트 통화', icon: 'fa-phone',
              run: (ui) => $j.F.Admin.Devil.TestCall.open({ labels: LayoutData.get('dvStatus', {}).master || {}, onDone: () => ui.reload() }) },
        ],
        columns: (ui) => [
            { title: '#', className: 'text-center', width: '60px', render: (x) => x.ref_id },
            { title: '심사', className: 'text-center', width: '76px', render: (x) => x.reason ? `<b>${ui.esc(x.reason)}</b>` : dash },
            { title: '업체',
              render: (x, d) => `<span class="tk-title">${ui.esc(x.company || '업체 미상')}</span>`
                  + (x.ceo ? ` <small class="text-muted">${ui.esc(x.ceo)}</small>` : '')
                  + `<div class="tk-sub">${ui.esc(ui.phone(x.phone)) || '&nbsp;'}</div>` + ui.memoLine(d) },
            { title: '체불액', className: 'text-right tk-nowrap', width: '100px',
              // 회수 줄에 누가 — 제보자(앱에서 직접) / 관리자 이름
              render: (x) => `<b>${ui.won(x.amount)}</b>${x.recovered_at ? `<div class="tk-sub">회수 ${ui.esc(x.recovered_at)}${x.recovered_by ? ' · ' + ui.esc(x.recovered_by) : ''}</div>` : ''}` },
            { title: '피해', className: 'text-center tk-nowrap', width: '90px',
              render: (x) => `${ui.esc(x.equip) || '-'}<div class="tk-sub">${ui.esc(x.damage_day)}</div>` },
            // 이름 = 그 회원의 악덕알리미 모바일(대리로그인) · #번호 = 회원 팝오버 — 옛 심사목록과 같다
            { title: '제보자', className: 'tk-nowrap', width: '150px',
              render: (x) => `<a href="javascript:;" data-tk-devilmb="${x.reporter_no}">${ui.esc(x.reporter_name || '회원')}</a>`
                  + `<small class="text-muted">#${$j.F.Admin.Member.Pop.cellNo(x.reporter_no)}</small>`
                  + `<div class="tk-sub">${ui.esc(ui.phone(x.reporter_phone))}</div>` },
            { title: '신고 상태', className: 'text-center', width: '90px',
              render: (x) => (x.status === 'APPROVED' && x.master_num
                      ? `<a href="javascript:;" data-tk-dvmaster="${x.master_num}" title="악덕마스터 보기">${ui.esc(x.status_label)}</a>`
                      : ui.esc(x.status_label))
                  + (x.defense_label ? `<div class="tk-sub">${ui.esc(x.defense_label)}</div>` : '') },
            { title: '등록일', className: 'text-center tk-nowrap', width: '90px', render: (x) => ui.when(x.created_at) },
        ],
    },

    /** 탈퇴 — 옛 탈퇴신청 목록(/adm/leave) */
    leave: {
        filters: [
            { key: 'status', type: 'seg', label: '신청상태', def: '1',
              options: [['all', '전체'], ['1', '신청'], ['2', '확인함'], ['3', '신청취소']] },
            { key: 'keyword', type: 'text', placeholder: '이름 / 회원번호', width: 150 },
        ],
        columns: (ui) => [
            { title: '신청일', className: 'text-center tk-nowrap', width: '90px', render: (x) => ui.when(x.created_at) },
            { title: '회원', className: 'text-center', width: '130px', render: (x) => ui.member(x.mb_no, x.mem_name) },
            { title: '소속', className: 'text-center', width: '70px', render: (x) => ui.esc(x.sosok) || dash },
            { title: '가입일', className: 'text-center tk-nowrap', width: '90px', render: (x) => ui.esc(x.joined_at) || dash },
            { title: '탈퇴사유', render: (x) => ui.esc(x.reason) || dash },
            { title: '관리자 메모', render: (x, d) => (ui.esc(x.admin_memo) || dash) + ui.memoLine(d) },
            { title: '신청상태', className: 'text-center', width: '70px', render: (x) => ui.esc(x.status_label) },
            { title: '회원상태', className: 'text-center', width: '70px', render: (x) => ui.esc(x.mem_status) },
        ],
    },

    /** 카드 통보 — 이노페이 노티 수신 전체. 원문은 받은 그대로(카드번호류는 수신 때 가렸다) */
    card_noti: {
        filters: [
            { key: 'kind', type: 'seg', label: '통보', def: '', options: [['', '전체'], ['85', '취소'], ['25', '승인']] },
            { key: 'keyword', type: 'text', placeholder: '주문번호(moid) · 거래번호', width: 190 },
        ],
        columns: (ui) => [
            { title: '받은 시각', className: 'text-center tk-nowrap', width: '90px', render: (x) => ui.when(x.received_at) },
            { title: '통보', className: 'text-center', width: '50px', render: (x) => ui.esc(x.kind) },
            { title: '회원', className: 'text-center', width: '120px',
              render: (x) => Number(x.mb_no) > 0 ? ui.member(x.mb_no, x.mem_name) : dash },
            { title: '금액', className: 'text-right tk-nowrap', width: '90px', render: (x) => ui.won(x.amt) },
            { title: '우리 결제', className: 'text-center tk-nowrap', width: '90px',
              render: (x) => x.pay_num ? `${ui.esc(x.pay_status)}<div class="tk-sub">#${x.pay_num}</div>` : '<span class="text-muted">없음</span>' },
            { title: '확인할 것', render: (x, d) => (x.check ? ui.esc(x.check) : '<span class="text-muted">-</span>') + ui.memoLine(d) },
            { title: '이노페이 원문', width: '260px',
              render: (x) => `<details><summary class="tk-sub">${ui.esc(x.moid) || '원문'} 보기</summary>`
                  + `<pre class="mb-0 small" style="white-space:pre-wrap;word-break:break-all">${ui.esc(JSON.stringify(x.raw, null, 2))}</pre>`
                  + `<div class="tk-sub">우리 응답: ${ui.esc(x.answer)}</div></details>` },
        ],
    },

    /** 환원보류 — 따로 있던 목록은 없었다(접수목록 프리셋). 처리창(WhanHold)과 같은 값 */
    whanwon: {
        filters: [
            // 임박 = 알림과 같은 조건(마지노선 15일 전부터, 지난 것 포함)
            { key: 'window', type: 'seg', label: '보류', def: 'soon', options: [['all', '전체'], ['soon', '임박']] },
            { key: 'keyword', type: 'text', placeholder: '오더번호 · 이름', width: 150 },
        ],
        columns: (ui) => [
            { title: '마지노선', className: 'text-center tk-nowrap', width: '100px',
              render: (x) => {
                  const left = Number(x.d_left);
                  const tag  = left < 0 ? `${-left}일 지남` : `D-${left}`;
                  return `${ui.esc(x.deadline)}<div class="${left <= 15 ? 'tk-late' : 'tk-sub'}">${tag}</div>`;
              } },
            { title: '오더', className: 'text-center tk-nowrap', width: '100px',
              render: (x) => `${ui.order(x.ordernum)}<div class="tk-sub">${ui.esc(x.work_day)} · ${ui.esc(x.car_type)}</div>` },
            { title: '장비대금', className: 'text-right tk-nowrap', width: '100px',
              render: (x) => `${ui.won(x.car_price)}${x.fee_rate ? `<div class="tk-sub">수수료 ${x.fee_rate}%</div>` : ''}` },
            { title: '발주자', className: 'text-center', width: '120px', render: (x) => ui.member(x.owner_no, x.owner_name) },
            { title: '수주자', className: 'text-center', width: '120px', render: (x) => ui.member(x.worker_no, x.worker_name) },
            { title: '보류 사유',
              render: (x, d) => (x.hold_memo
                  ? `${ui.esc(x.hold_memo)}<div class="tk-sub">${ui.esc((x.hold_at || '').slice(0, 10))}</div>`
                  : '<span class="text-muted">기록 없음</span>') + ui.memoLine(d) },
            { title: '오더상태', className: 'text-center', width: '70px', render: (x) => ui.esc(x.status_label) },
        ],
    },
};

export default Views;
